import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft,
  Zap,
  Brain,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  TrendingDown,
  Minus,
  Lock,
  LogIn,
  ChevronRight,
  RotateCcw,
  Trophy,
  Target,
  Flame
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";

const DifficultyBadge = ({ difficulty }) => {
  const config = {
    easy: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Easy" },
    medium: { color: "bg-amber-100 text-amber-700 border-amber-200", label: "Medium" },
    hard: { color: "bg-rose-100 text-rose-700 border-rose-200", label: "Hard" }
  };
  const { color, label } = config[difficulty] || config.medium;
  
  return (
    <Badge className={`${color} border font-medium`}>
      {label}
    </Badge>
  );
};

const DifficultyIndicator = ({ difficulty, showLabel = true }) => {
  const config = {
    easy: { icon: TrendingDown, color: "text-emerald-500", bg: "bg-emerald-100" },
    medium: { icon: Minus, color: "text-amber-500", bg: "bg-amber-100" },
    hard: { icon: TrendingUp, color: "text-rose-500", bg: "bg-rose-100" }
  };
  const { icon: Icon, color, bg } = config[difficulty] || config.medium;
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      {showLabel && <span className={`text-sm font-medium ${color}`}>{difficulty}</span>}
    </div>
  );
};

export default function AdaptiveQuizPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isPremium, loading: authLoading, authChecked } = useAuth();
  
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [progress, setProgress] = useState({ answered: 0, correct: 0, total: 10, current_difficulty: "medium", accuracy: 0 });
  const [isComplete, setIsComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [difficultyMessage, setDifficultyMessage] = useState(null);
  
  const category = searchParams.get("category");
  const questionCount = parseInt(searchParams.get("count") || "10");

  const startQuiz = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/adaptive-quiz/start`, {
        category: category || null,
        question_count: questionCount
      });
      
      setSessionId(response.data.session_id);
      setCurrentQuestion(response.data.current_question);
      setProgress(response.data.progress);
      setQuestionStartTime(Date.now());
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start adaptive quiz");
      navigate("/quizzes");
    } finally {
      setLoading(false);
    }
  }, [category, questionCount, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      startQuiz();
    }
  }, [isAuthenticated, startQuiz]);

  const submitAnswer = async () => {
    if (!selectedAnswer || !sessionId || submitting) return;
    
    setSubmitting(true);
    const timeTaken = Math.round((Date.now() - questionStartTime) / 1000);
    
    try {
      const response = await axios.post(`${API}/adaptive-quiz/answer`, {
        session_id: sessionId,
        question_id: currentQuestion.id,
        selected_answer: selectedAnswer,
        time_taken: timeTaken
      });
      
      setFeedback(response.data.feedback);
      
      if (response.data.complete) {
        setIsComplete(true);
        setResults(response.data.results);
      } else {
        setProgress(response.data.progress);
        if (response.data.feedback.difficulty_change) {
          setDifficultyMessage(response.data.feedback.difficulty_change);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit answer");
    } finally {
      setSubmitting(false);
    }
  };

  const nextQuestion = (response) => {
    // This is called after showing feedback
    setSelectedAnswer(null);
    setFeedback(null);
    setDifficultyMessage(null);
    
    // Get next question from the last response we stored
    if (response?.next_question) {
      setCurrentQuestion(response.next_question);
      setQuestionStartTime(Date.now());
    }
  };

  const handleContinue = async () => {
    // Fetch current session state which includes next question
    try {
      const response = await axios.get(`${API}/adaptive-quiz/session/${sessionId}`);
      if (response.data.current_question) {
        setCurrentQuestion(response.data.current_question);
        setSelectedAnswer(null);
        setFeedback(null);
        setDifficultyMessage(null);
        setQuestionStartTime(Date.now());
      }
    } catch (error) {
      // If session expired, navigate away
      toast.error("Session expired");
      navigate("/quizzes");
    }
  };

  const cancelQuiz = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/adaptive-quiz/session/${sessionId}`);
      } catch (error) {
        // Ignore errors on cancel
      }
    }
    navigate("/quizzes");
  };

  // Show login required screen if not authenticated
  if (authChecked && !authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-teal-50/20 flex items-center justify-center p-6">
        <motion.div 
          className="max-w-md w-full text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-amber-100 p-8 shadow-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3">Login Required</h1>
            <p className="text-slate-600 mb-6">
              Sign in to access adaptive quizzes that adjust to your skill level.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate("/auth")}
                className="w-full bg-teal-500 hover:bg-teal-600 text-white py-6 font-semibold"
                data-testid="login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign In / Create Account
              </Button>
              <Button 
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-slate-600"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-600">Preparing adaptive quiz...</p>
        </div>
      </div>
    );
  }

  // Results screen
  if (isComplete && results) {
    const percentage = results.percentage;
    const performanceLevel = percentage >= 80 ? "Excellent" : percentage >= 60 ? "Good" : percentage >= 40 ? "Keep Practicing" : "Needs Work";
    const performanceColor = percentage >= 80 ? "text-emerald-600" : percentage >= 60 ? "text-blue-600" : percentage >= 40 ? "text-amber-600" : "text-rose-600";
    
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl overflow-hidden">
                <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
              </div>
              <span className="font-bold text-xl tracking-tight">Adaptive Quiz Complete</span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center mb-8"
          >
            <div className={`w-24 h-24 mx-auto mb-6 rounded-full ${percentage >= 60 ? 'bg-emerald-100' : 'bg-amber-100'} flex items-center justify-center`}>
              <Trophy className={`w-12 h-12 ${percentage >= 60 ? 'text-emerald-600' : 'text-amber-600'}`} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="results-title">{performanceLevel}!</h1>
            <p className={`text-5xl font-bold ${performanceColor} mb-2`} data-testid="results-score">{percentage}%</p>
            <p className="text-slate-600">{results.score} out of {results.total} correct</p>
          </motion.div>

          {/* Difficulty Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
          >
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-violet-600" />
              Performance by Difficulty
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {["easy", "medium", "hard"].map((diff) => {
                const stats = results.difficulty_breakdown?.[diff] || { correct: 0, total: 0 };
                const diffPercent = stats.total > 0 ? Math.round(stats.correct / stats.total * 100) : 0;
                return (
                  <div key={diff} className="text-center p-4 rounded-xl bg-slate-50">
                    <DifficultyIndicator difficulty={diff} showLabel={false} />
                    <p className="text-sm text-slate-500 capitalize mt-2">{diff}</p>
                    <p className="text-xl font-bold text-slate-900">{stats.correct}/{stats.total}</p>
                    <p className="text-sm text-slate-500">{diffPercent}%</p>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => {
                setIsComplete(false);
                setResults(null);
                setFeedback(null);
                setSelectedAnswer(null);
                startQuiz();
              }}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="try-again-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/quizzes")}
              data-testid="back-to-quizzes-btn"
            >
              Back to Quizzes
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate("/analytics")}
            >
              View Analytics
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={cancelQuiz}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <span className="font-bold text-lg tracking-tight block">Adaptive Quiz</span>
                  {category && <span className="text-xs text-slate-500">{category}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <DifficultyBadge difficulty={progress.current_difficulty} />
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">
                  {progress.answered}/{progress.total}
                </p>
                <p className="text-xs text-slate-500">
                  {progress.accuracy || 0}% accuracy
                </p>
              </div>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4">
            <Progress 
              value={(progress.answered / progress.total) * 100} 
              className="h-2"
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Difficulty change message */}
        <AnimatePresence>
          {difficultyMessage && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-100 to-purple-100 border border-violet-200 text-center"
            >
              <p className="text-violet-700 font-medium flex items-center justify-center gap-2">
                <Flame className="w-5 h-5" />
                {difficultyMessage}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question Card */}
        {currentQuestion && (
          <motion.div
            key={currentQuestion.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl border border-slate-200 p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline" className="text-slate-600">
                Question {currentQuestion.question_number}
              </Badge>
              <div className="flex items-center gap-2">
                <DifficultyBadge difficulty={currentQuestion.difficulty} />
                <Badge variant="outline">{currentQuestion.category}</Badge>
              </div>
            </div>

            <h2 className="text-xl font-semibold text-slate-900 mb-6" data-testid="question-text">
              {currentQuestion.text}
            </h2>

            {/* Answer Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = selectedAnswer === option;
                const showFeedback = feedback !== null;
                const isCorrect = option === feedback?.correct_answer;
                const isWrong = showFeedback && isSelected && !feedback?.is_correct;
                
                let optionClass = "border-slate-200 hover:border-teal-300 hover:bg-teal-50/50";
                if (isSelected && !showFeedback) {
                  optionClass = "border-teal-500 bg-teal-50";
                } else if (showFeedback) {
                  if (isCorrect) {
                    optionClass = "border-emerald-500 bg-emerald-50";
                  } else if (isWrong) {
                    optionClass = "border-rose-500 bg-rose-50";
                  }
                }
                
                return (
                  <button
                    key={index}
                    onClick={() => !showFeedback && setSelectedAnswer(option)}
                    disabled={showFeedback}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${optionClass} ${showFeedback ? 'cursor-default' : 'cursor-pointer'}`}
                    data-testid={`option-${index}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-slate-900">{option}</span>
                      {showFeedback && isCorrect && (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      )}
                      {showFeedback && isWrong && (
                        <XCircle className="w-5 h-5 text-rose-600" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Feedback */}
            <AnimatePresence>
              {feedback && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-6 p-4 rounded-xl ${feedback.is_correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {feedback.is_correct ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-rose-600" />
                    )}
                    <span className={`font-semibold ${feedback.is_correct ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {feedback.is_correct ? "Correct!" : "Incorrect"}
                    </span>
                  </div>
                  {feedback.explanation && (
                    <p className="text-sm text-slate-700">{feedback.explanation}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          {!feedback ? (
            <Button
              onClick={submitAnswer}
              disabled={!selectedAnswer || submitting}
              className="bg-teal-600 hover:bg-teal-700 px-8"
              data-testid="submit-btn"
            >
              {submitting ? "Submitting..." : "Submit Answer"}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleContinue}
              className="bg-violet-600 hover:bg-violet-700 px-8"
              data-testid="next-btn"
            >
              Next Question
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
